import { cn } from '@/ui/lib/utils'

export interface QuantIndicatorRowProps {
  name: string
  /** `SMA · 20` (see `indicatorMeta`). */
  meta: string
  color: string
  on: boolean
  /** Opens the settings modal (the row body). */
  onEdit: () => void
  onToggle: () => void
}

/**
 * One row of the Manual trade Indicators rail (spec 036, handoff 4.4): colour
 * swatch (35% when off), truncating name and meta, and a 38px On/Off button.
 * The row has a fixed height; a long name truncates and carries a `title`.
 * The toggle's accessible name is fixed (never the indicator's name).
 */
export function QuantIndicatorRow({ name, meta, color, on, onEdit, onToggle }: QuantIndicatorRowProps) {
  return (
    <div data-indicator-row className="flex h-[52px] items-center gap-2 pr-2.5 pl-4 hover:bg-accent-wash">
      <button
        type="button"
        title="Edit settings"
        onClick={onEdit}
        className="flex h-full min-w-0 flex-1 cursor-pointer items-center gap-2.5 border-0 bg-transparent p-0 text-left text-ink"
      >
        <i
          aria-hidden="true"
          style={{ background: color }}
          className={cn('block h-2.5 w-2.5 shrink-0 border border-ink-25', !on && 'opacity-35')}
        />
        <span className="flex min-w-0 flex-col gap-0.5">
          <span title={name} className={cn('truncate font-mono text-[12px] font-semibold', on ? 'text-ink' : 'text-ink-50')}>
            {name}
          </span>
          <span title={meta} className="truncate font-mono text-[10px] text-ink-50">
            {meta}
          </span>
        </span>
      </button>
      <button
        type="button"
        aria-label="Toggle indicator"
        aria-pressed={on}
        title={on ? 'Hide on chart' : 'Show on chart'}
        onClick={onToggle}
        className={cn(
          'w-[38px] shrink-0 cursor-pointer border py-[3px] font-sans text-[9.5px] font-semibold tracking-[0.16em] uppercase',
          on ? 'border-ink bg-ink text-paper' : 'border-ink-25 bg-transparent text-ink-50',
        )}
      >
        {on ? 'On' : 'Off'}
      </button>
    </div>
  )
}
